import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  X,
  Plus,
  Calendar,
  Download,
  ChevronDown,
} from "lucide-react";
import { useDebounce } from "use-debounce";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getDepartments } from "../../../api/hrm/plans/info";
import { hasPermission } from "../../../utils/permissionUtils";
import { defaultPlanMonthString, getDefaultPlanFilters } from "./planFilterDefaults";

const TEXT = {
  searchPlaceholder: "Tìm theo tiêu đề hoặc nội dung",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  department: "Bộ phận",
  category: "Phân loại",
  planType: "Loại kế hoạch",
  monthPicker: "Tháng áp dụng",
  status: "Trạng thái",
  clear: "Xóa lọc",
  apply: "Áp dụng",
  templates: "Tải biểu mẫu",
  create: "Tạo kế hoạch",
};

const PlanFilter = ({ onNewClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const templateMenuRef = useRef(null);

  const [filters, setFilters] = useState(() => getDefaultPlanFilters());

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [debouncedKeyword] = useDebounce(filters.keyword, 500);

  const categoryOptions = [
    { value: "branch", label: "Chi nhánh" },
    { value: "department", label: "Phòng ban" },
    { value: "personal", label: "Cá nhân" },
  ];
  const typeOptions = [
    { value: "finance", label: "Tài chính" },
    { value: "work", label: "Công việc" },
  ];
  const statusOptions = [
    { value: "draft", label: "Nháp" },
    { value: "pending", label: "Chờ xử lý" },
    { value: "waiting_approval", label: "Chờ phê duyệt" },
    { value: "approved", label: "Đã duyệt" },
    { value: "rejected", label: "Từ chối" },
  ];

  const templateLinks = {
    plan_tasks: `${window.location.origin}/templates/hrm/plan/frm_plan_tasks.docx`,
    plan_finance: `${window.location.origin}/templates/hrm/plan/frm_plan_finance.docx`,
  };

  const isFiltering =
    filters.keyword !== "" ||
    filters.branch_id !== "" ||
    filters.department_id !== "" ||
    filters.plan_category !== "" ||
    filters.plan_type !== "" ||
    filters.status !== "" ||
    (filters.month !== "" && filters.month !== defaultPlanMonthString());

  useEffect(() => {
    onFilterChange?.({ ...filtersRef.current, keyword: debouncedKeyword });
  }, [debouncedKeyword]);

  useEffect(() => {
    onFilterChange?.(filtersRef.current);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const branches = res.items || res;
        setBranchOptions(branches.map((b) => ({ value: b.id, label: b.name })));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!filters.branch_id) {
      setDepartmentOptions([]);
      setFilters((prev) => ({ ...prev, department_id: "" }));
      return;
    }
    getDepartments(filters.branch_id)
      .then((list) =>
        setDepartmentOptions(list.map((d) => ({ value: d.id, label: d.name })))
      )
      .catch(console.error);
  }, [filters.branch_id]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!templateMenuRef.current?.contains(e.target)) setShowTemplateMenu(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const monthInputClassName =
    "shrink-0 rounded-2xl border-2 border-transparent bg-gray-50 px-3 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100";

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = getDefaultPlanFilters();
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
          : "border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:text-red-600"
      }`}
    >
      <Icon size={18} strokeWidth={2} />
    </button>
  );

  const BranchField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.branch}
      </label>
      <Select
        options={branchOptions}
        placeholder="— Chọn —"
        styles={selectStyles}
        value={branchOptions.find((opt) => opt.value === filters.branch_id) || null}
        onChange={(opt) => updateFilter("branch_id", opt?.value || "")}
        isClearable
        menuPortalTarget={document.body}
      />
    </div>
  );

  const DepartmentField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.department}
      </label>
      <Select
        options={departmentOptions}
        placeholder="— Chọn —"
        styles={selectStyles}
        value={departmentOptions.find((opt) => opt.value === filters.department_id) || null}
        onChange={(opt) => updateFilter("department_id", opt?.value || "")}
        isClearable
        isDisabled={!filters.branch_id}
        menuPortalTarget={document.body}
      />
    </div>
  );

  const CategoryField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.category}
      </label>
      <Select
        options={categoryOptions}
        placeholder="— Chọn —"
        styles={selectStyles}
        value={categoryOptions.find((opt) => opt.value === filters.plan_category) || null}
        onChange={(opt) => updateFilter("plan_category", opt?.value || "")}
        isClearable
        menuPortalTarget={document.body}
      />
    </div>
  );

  const TypeField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.planType}
      </label>
      <Select
        options={typeOptions}
        placeholder="— Chọn —"
        styles={selectStyles}
        value={typeOptions.find((opt) => opt.value === filters.plan_type) || null}
        onChange={(opt) => updateFilter("plan_type", opt?.value || "")}
        isClearable
        menuPortalTarget={document.body}
      />
    </div>
  );

  const MonthField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.monthPicker}
      </label>
      <input
        type="month"
        value={filters.month}
        onChange={(e) => updateFilter("month", e.target.value)}
        aria-label={TEXT.monthPicker}
        className={`h-12 w-full min-w-0 ${monthInputClassName}`}
      />
    </div>
  );

  const StatusField = () => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {TEXT.status}
      </label>
      <Select
        options={statusOptions}
        placeholder="— Chọn —"
        styles={selectStyles}
        value={statusOptions.find((opt) => opt.value === filters.status) || null}
        onChange={(opt) => updateFilter("status", opt?.value || "")}
        isClearable
        menuPortalTarget={document.body}
      />
    </div>
  );

  const SecondaryActions = () => (
    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
      {hasPermission("hrm.plans", "C") && (
        <div className="relative" ref={templateMenuRef}>
          <button
            type="button"
            onClick={() => setShowTemplateMenu((v) => !v)}
            className="inline-flex h-11 items-center gap-2 rounded-xl border-2 border-red-600 bg-white px-4 text-xs font-black uppercase tracking-wide text-red-700 shadow-sm transition hover:bg-red-50 active:scale-[0.98]"
          >
            <Download size={18} className="shrink-0" strokeWidth={2.25} />
            {TEXT.templates}
            <ChevronDown size={16} className="shrink-0 opacity-70" strokeWidth={2.25} />
          </button>
          {showTemplateMenu && (
            <div className="absolute right-0 z-[100] mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
              <a
                href={templateLinks.plan_tasks}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setShowTemplateMenu(false)}
              >
                Kế hoạch công việc
              </a>
              <a
                href={templateLinks.plan_finance}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setShowTemplateMenu(false)}
              >
                Kế hoạch tài chính
              </a>
            </div>
          )}
        </div>
      )}

      {hasPermission("hrm.plans", "C") && (
        <button
          type="button"
          onClick={onNewClick}
          className="inline-flex h-11 items-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 text-xs font-black uppercase tracking-wide text-white shadow-md shadow-red-100 transition hover:shadow-lg active:scale-[0.98]"
        >
          <Plus size={18} strokeWidth={2.5} />
          {TEXT.create}
        </button>
      )}
    </div>
  );

  const AdvancedFields = () => (
    <>
      <BranchField />
      <DepartmentField />
      <CategoryField />
      <TypeField />
      <MonthField />
      <StatusField />
    </>
  );

  if (isMobile) {
    return (
      <div className="relative mb-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <label
              className="relative flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition-all duration-200 hover:border-red-300 hover:text-red-600 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100"
              title={TEXT.monthPicker}
            >
              <Calendar size={18} strokeWidth={2.25} className="pointer-events-none" aria-hidden />
              <input
                type="month"
                value={filters.month}
                onChange={(e) => updateFilter("month", e.target.value)}
                aria-label={TEXT.monthPicker}
                className="absolute inset-0 cursor-pointer rounded-xl opacity-0"
              />
            </label>
            <div className="group relative min-w-0 flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-red-500"
                strokeWidth={2.25}
              />
              <input
                type="text"
                placeholder={TEXT.searchPlaceholder}
                value={filters.keyword}
                onChange={(e) => updateFilter("keyword", e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100"
              />
            </div>
            <ActionButton icon={RotateCw} onClick={clearFilters} title={TEXT.refresh} />
            <ActionButton
              icon={SlidersHorizontal}
              onClick={() => setAdvanced(true)}
              title={TEXT.advanced}
            />
          </div>
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  clearFilters();
                  setAdvanced(false);
                }}
                className="flex-1 rounded-2xl border border-gray-200 py-4 text-sm font-bold text-gray-600 transition hover:bg-gray-50"
              >
                {TEXT.clear}
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 rounded-2xl bg-red-600 py-4 text-sm font-black uppercase tracking-wide text-white shadow-lg shadow-red-200 transition hover:bg-red-700"
              >
                {TEXT.apply}
              </button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <AdvancedFields />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="relative mb-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
          <input
            type="month"
            value={filters.month}
            onChange={(e) => updateFilter("month", e.target.value)}
            aria-label={TEXT.monthPicker}
            title={TEXT.monthPicker}
            className={`h-[42px] w-full min-w-0 max-w-[12.5rem] sm:w-auto ${monthInputClassName}`}
          />
          <div className="group relative min-w-0 max-w-xl flex-1">
            <Search
              className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-red-500"
              strokeWidth={2.25}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full rounded-2xl border-2 border-transparent bg-gray-50 py-2.5 pl-11 pr-4 text-sm font-medium text-gray-800 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0"
            />
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <ActionButton icon={RotateCw} onClick={clearFilters} title={TEXT.refresh} />
            <ActionButton
              icon={SlidersHorizontal}
              onClick={() => setAdvanced(!advanced)}
              active={advanced}
              title={TEXT.advanced}
            />
            {isFiltering && (
              <ActionButton
                icon={X}
                onClick={clearFilters}
                title={TEXT.clear}
              />
            )}
          </div>
        </div>

        <SecondaryActions />
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-100 pt-6 md:grid-cols-2">
              <AdvancedFields />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlanFilter;
